import React, { useState, useEffect } from "react";
import axios from "axios";
import { Icon } from "./Icon";

interface BotCredentials {
  id: string;
  botUsername: string;
  twitchUserId?: string;
  accessToken: string;
  refreshToken: string;
  tokenExpiresAt?: string;
  lastTokenRefresh?: string;
  isPublic: boolean;
  createdBy?: {
    id: string;
    username: string;
  };
  createdById?: string;
  createdAt: string;
  updatedAt: string;
  activeBotCount?: number;
  totalBotCount?: number;
  dashboardScopes?: string[] | null;
  dashboardTokenLinkedAt?: string | null;
}

// #118: the bot account's grant to the dashboard app, used for chat subs.
interface ChatLinkStatus {
  linked: boolean;
  status: string;
  clientIdMatches: boolean;
  scopes: string[];
  linkedAt: string | null;
}

interface TwitchApp {
  id: string;
  type: "dashboard" | "bot";
  clientId: string;
  clientSecret: string;
  callbackUrl?: string;
  createdAt: string;
  updatedAt: string;
  isEditable: boolean;
}

interface BotAdminProps {
  authToken: string;
}

const BotAdmin: React.FC<BotAdminProps> = ({ authToken }) => {
  const [botCredentials, setBotCredentials] = useState<BotCredentials[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingBot, setEditingBot] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    botUsername: "",
    accessToken: "",
    refreshToken: "",
    twitchUserId: "",
    autoRefresh: false,
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [clientId, setClientId] = useState("");
  const [botApp, setBotApp] = useState<TwitchApp | null>(null);
  const [botScopes, setBotScopes] = useState<string[]>([]);
  const [botScopesUrlParam, setBotScopesUrlParam] = useState<string>("");
  const [chatLinkStatus, setChatLinkStatus] = useState<
    Record<string, ChatLinkStatus>
  >({});

  // #118: log in to Twitch as the bot account and grant the dashboard app
  // user:bot + user:read:chat, so HAL's chat subs use the app broadcasters
  // granted channel:bot to.
  const handleLinkChat = async (botUsername: string) => {
    try {
      const response = await axios.get(
        `/api/auth/bot-chat-link/${botUsername}/url`,
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Error starting chat link:", error);
      alert("Failed to start the chat link");
    }
  };

  const checkChatLink = async (botUsername: string) => {
    try {
      const response = await axios.get(
        `/api/auth/bot-chat-link/${botUsername}/status`,
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      setChatLinkStatus((prev) => ({ ...prev, [botUsername]: response.data }));
    } catch (error) {
      console.error("Error checking chat link:", error);
    }
  };

  // Token generation instructions component
  const _TokenGenerationInstructions = () => (
    <div
      style={{
        backgroundColor: "#1a202c",
        padding: "15px",
        borderRadius: "6px",
        marginBottom: "20px",
        border: "1px solid #4a5568",
      }}
    >
      <h4 style={{ color: "#48BB78", marginTop: 0, marginBottom: "10px" }}>
        How to Generate Bot Tokens
      </h4>
      {botApp && botApp.clientId && (
        <div
          style={{
            backgroundColor: "#2d3748",
            padding: "10px",
            borderRadius: "4px",
            marginBottom: "15px",
            fontSize: "13px",
            color: "#48BB78",
          }}
        >
          <strong>
            <Icon
              name="check"
              size={14}
              color="#10B981"
              style={{ verticalAlign: "-2px", marginRight: "4px" }}
            />
            Bot App Configured:
          </strong>{" "}
          Using Twitch App Client ID:{" "}
          <code
            style={{
              backgroundColor: "#1a202c",
              padding: "2px 6px",
              borderRadius: "3px",
            }}
          >
            {botApp.clientId}
          </code>
        </div>
      )}
      <div
        style={{
          backgroundColor: "#2d3748",
          padding: "10px",
          borderRadius: "4px",
          marginBottom: "15px",
          fontSize: "13px",
          color: "#fbbf24",
        }}
      >
        <strong>
          <Icon
            name="alert-triangle"
            size={14}
            style={{ verticalAlign: "-2px", marginRight: "4px" }}
          />
          Important:
        </strong>{" "}
        The bot needs its own Twitch application, separate from the main app
        used for user login. This ensures proper isolation and permissions.
      </div>
      <ol
        style={{
          color: "#cbd5e0",
          fontSize: "14px",
          paddingLeft: "20px",
          margin: 0,
        }}
      >
        <li style={{ marginBottom: "8px" }}>
          Create a <strong>separate</strong> Twitch application for your bot at{" "}
          <a
            href="https://dev.twitch.tv/console/apps"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#9146FF", textDecoration: "underline" }}
          >
            dev.twitch.tv/console/apps
          </a>
          <div
            style={{
              marginTop: "5px",
              fontSize: "13px",
              color: "#a0aec0",
              paddingLeft: "20px",
            }}
          >
            • Name it something like &quot;YourBotName Bot&quot; to distinguish
            it from your main app
            <br />• This should be different from your main application that
            users log in with
          </div>
        </li>
        <li style={{ marginBottom: "8px" }}>
          Set OAuth Redirect URL to:{" "}
          <code
            style={{
              backgroundColor: "#2d3748",
              padding: "2px 6px",
              borderRadius: "3px",
            }}
          >
            {window.location.hostname === "localhost"
              ? "http://localhost/api"
              : "https://hal8580.com/api"}
            /auth/bot-callback
          </code>
          <div
            style={{
              marginTop: "5px",
              fontSize: "13px",
              color: "#a0aec0",
              paddingLeft: "20px",
            }}
          >
            • Note: This is different from the main app callback
            (/auth/callback)
            <br />• Helps distinguish bot token generation from user login
          </div>
        </li>
        <li style={{ marginBottom: "8px" }}>
          Generate tokens using this URL:
          <div style={{ marginTop: "8px", marginBottom: "8px" }}>
            {!botApp || !botApp.clientId ? (
              <>
                <input
                  type="text"
                  placeholder="Enter your Twitch Client ID"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  style={{
                    width: "300px",
                    padding: "6px 10px",
                    border: "1px solid #4a5568",
                    borderRadius: "4px",
                    backgroundColor: "#1a202c",
                    color: "#e2e8f0",
                    fontSize: "14px",
                    marginRight: "10px",
                  }}
                />
                {!botApp && (
                  <div
                    style={{
                      marginTop: "5px",
                      fontSize: "12px",
                      color: "#fbbf24",
                    }}
                  >
                    <Icon
                      name="alert-triangle"
                      size={14}
                      style={{ verticalAlign: "-2px", marginRight: "4px" }}
                    />
                    No bot app configured. Please configure it in{" "}
                    <a
                      href="/dashboard/admin/twitch-apps"
                      style={{ color: "#9146FF" }}
                    >
                      Twitch Apps
                    </a>{" "}
                    first.
                  </div>
                )}
              </>
            ) : null}
            {clientId && (
              <>
                <button
                  onClick={() => {
                    const redirectUri =
                      window.location.hostname === "localhost"
                        ? "http://localhost/api/auth/bot-callback"
                        : "https://hal8580.com/api/auth/bot-callback";
                    const url = `https://id.twitch.tv/oauth2/authorize?response_type=token&client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${botScopesUrlParam}`;
                    window.open(url, "_blank");
                  }}
                  style={{
                    padding: "6px 12px",
                    backgroundColor: "#9146FF",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "14px",
                    marginRight: "10px",
                  }}
                >
                  Generate Token (No Refresh)
                </button>
                <button
                  onClick={() => {
                    const redirectUri =
                      window.location.hostname === "localhost"
                        ? "http://localhost/api/auth/bot-callback"
                        : "https://hal8580.com/api/auth/bot-callback";
                    const url = `https://id.twitch.tv/oauth2/authorize?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${botScopesUrlParam}`;
                    window.open(url, "_blank");
                  }}
                  style={{
                    padding: "6px 12px",
                    backgroundColor: "#48BB78",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "14px",
                  }}
                >
                  Generate Code (For Refresh Token)
                </button>
              </>
            )}
          </div>
        </li>
      </ol>
    </div>
  );

  useEffect(() => {
    fetchBotCredentials();
    fetchBotApp();
    fetchScopes();

    // Check for success query parameter
    const urlParams = new URLSearchParams(window.location.search);
    const chatLink = urlParams.get("chatLink");
    if (chatLink) {
      alert(
        chatLink === "ok"
          ? `Chat link stored for ${urlParams.get("bot")}. Chat subscriptions are moving to the dashboard app.`
          : `Chat link failed: ${urlParams.get("reason") || "unknown error"}`,
      );
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    if (urlParams.get("success") === "true") {
      const botUsername = urlParams.get("bot");
      if (botUsername) {
        // Close any open forms
        setShowAddForm(false);
        setEditingBot(null);
        // Reset form data
        setFormData({
          botUsername: "",
          accessToken: "",
          refreshToken: "",
          twitchUserId: "",
          autoRefresh: false,
        });
        // Show success message
        alert(`Successfully updated tokens for bot: ${botUsername}`);
        // Refresh the bot list
        fetchBotCredentials();
      }
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [authToken]);

  const fetchBotCredentials = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/bot/credentials", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setBotCredentials(response.data);
    } catch (error) {
      console.error("Error fetching bot credentials:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBotApp = async () => {
    try {
      const response = await axios.get("/api/twitch-apps", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const apps = response.data;
      const botAppData = apps.find((app: TwitchApp) => app.type === "bot");
      if (botAppData) {
        setBotApp(botAppData);
        setClientId(botAppData.clientId);
      }
    } catch (error) {
      console.error("Error fetching bot app configuration:", error);
    }
  };

  const fetchScopes = async () => {
    try {
      const response = await axios.get("/api/config/scopes");
      const { bot } = response.data;
      setBotScopes(bot.scopes);
      setBotScopesUrlParam(bot.scopes.join("+"));
    } catch (error) {
      console.error("Error fetching scopes configuration:", error);
      // Fallback to empty - will show error if user tries to authenticate
      setBotScopes([]);
      setBotScopesUrlParam("");
    }
  };

  const handleEdit = (bot: BotCredentials) => {
    setEditingBot(bot.botUsername);
    setFormData({
      botUsername: bot.botUsername,
      accessToken: "",
      refreshToken: "",
      twitchUserId: bot.twitchUserId || "",
      autoRefresh: false,
    });
  };

  const _handleSave = async () => {
    try {
      const dataToSend: any = {};
      if (formData.accessToken) dataToSend.accessToken = formData.accessToken;
      if (formData.refreshToken)
        dataToSend.refreshToken = formData.refreshToken;
      if (formData.twitchUserId)
        dataToSend.twitchUserId = formData.twitchUserId;
      if (formData.autoRefresh !== undefined)
        dataToSend.autoRefresh = formData.autoRefresh;

      if (editingBot) {
        await axios.put(`/api/bot/credentials/${editingBot}`, dataToSend, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
      } else {
        await axios.post(
          "/api/bot/credentials",
          {
            ...formData,
          },
          {
            headers: { Authorization: `Bearer ${authToken}` },
          },
        );
      }

      await fetchBotCredentials();
      setEditingBot(null);
      setShowAddForm(false);
      setFormData({
        botUsername: "",
        accessToken: "",
        refreshToken: "",
        twitchUserId: "",
        autoRefresh: false,
      });
    } catch (error) {
      console.error("Error saving bot credentials:", error);
      alert("Failed to save bot credentials");
    }
  };

  const handleDelete = async (botUsername: string) => {
    if (
      !window.confirm(
        `Are you sure you want to delete bot credentials for ${botUsername}?`,
      )
    ) {
      return;
    }

    try {
      await axios.delete(`/api/bot/credentials/${botUsername}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      await fetchBotCredentials();
    } catch (error) {
      console.error("Error deleting bot credentials:", error);
      alert("Failed to delete bot credentials");
    }
  };

  const handleRefresh = async (botUsername: string) => {
    try {
      await axios.post(
        `/api/bot/credentials/${botUsername}/refresh`,
        {},
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );
      await fetchBotCredentials();
      alert(`Token refreshed successfully for ${botUsername}`);
    } catch (error) {
      console.error("Error refreshing token:", error);
      alert("Failed to refresh token");
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
        Loading bot credentials...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "20px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "30px",
        }}
      >
        <h2 style={{ color: "#e2e8f0", margin: 0 }}>
          Bot Credentials Management
        </h2>
        <button
          onClick={() => setShowAddForm(true)}
          style={{
            padding: "10px 20px",
            backgroundColor: "#9146FF",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          Add New Bot
        </button>
      </div>

      {showAddForm && (
        <div
          style={{
            backgroundColor: "#2d3748",
            padding: "20px",
            borderRadius: "8px",
            marginBottom: "20px",
            border: "1px solid #4a5568",
          }}
        >
          <h3 style={{ color: "#9146FF", marginTop: 0 }}>
            Add New Bot Credentials
          </h3>

          <div style={{ display: "grid", gap: "15px" }}>
            {botApp && botApp.clientId ? (
              <div
                style={{
                  backgroundColor: "#1a202c",
                  padding: "20px",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    backgroundColor: "#276749",
                    padding: "15px",
                    borderRadius: "4px",
                    marginBottom: "15px",
                    fontSize: "14px",
                    color: "#F0FDF4",
                  }}
                >
                  <strong>
                    <Icon
                      name="sparkles"
                      size={14}
                      style={{ verticalAlign: "-2px", marginRight: "4px" }}
                    />
                    Simplified Flow:
                  </strong>{" "}
                  The bot username will be automatically detected from the
                  account you authenticate with!
                </div>
                <div
                  style={{
                    backgroundColor: "#ef4444",
                    padding: "10px",
                    borderRadius: "4px",
                    marginBottom: "15px",
                    fontSize: "13px",
                  }}
                >
                  <strong>
                    <Icon
                      name="alert-triangle"
                      size={14}
                      style={{ verticalAlign: "-2px", marginRight: "4px" }}
                    />
                    CRITICAL:
                  </strong>{" "}
                  You MUST:
                  <ol style={{ margin: "5px 0 0 20px", paddingLeft: "0" }}>
                    <li>Copy the URL below</li>
                    <li>Open it in an incognito/private window</li>
                    <li>Log in as the bot account (NOT as yourself!)</li>
                    <li>Authorize the bot app</li>
                  </ol>
                </div>
                <div>
                  <label
                    style={{
                      color: "#cbd5e0",
                      fontSize: "12px",
                      display: "block",
                      marginBottom: "8px",
                    }}
                  >
                    OAuth URL for Bot Authentication:
                  </label>
                  <div
                    style={{
                      backgroundColor: "#2d3748",
                      padding: "10px",
                      borderRadius: "4px",
                      wordBreak: "break-all",
                      fontFamily: "monospace",
                      fontSize: "11px",
                      color: "#e2e8f0",
                      marginBottom: "10px",
                      maxHeight: "100px",
                      overflow: "auto",
                    }}
                  >
                    {(() => {
                      const redirectUri =
                        window.location.hostname === "localhost"
                          ? "http://localhost/api/auth/bot-callback"
                          : "https://hal8580.com/api/auth/bot-callback";
                      const state = btoa(
                        JSON.stringify({
                          botUsername: "auto-detect",
                          action: "create",
                          requestedScopes: botScopes,
                        }),
                      );
                      return `https://id.twitch.tv/oauth2/authorize?response_type=code&client_id=${botApp.clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${botScopesUrlParam}&state=${state}`;
                    })()}
                  </div>
                  <button
                    onClick={() => {
                      const redirectUri =
                        window.location.hostname === "localhost"
                          ? "http://localhost/api/auth/bot-callback"
                          : "https://hal8580.com/api/auth/bot-callback";
                      const state = btoa(
                        JSON.stringify({
                          botUsername: "auto-detect",
                          action: "create",
                          requestedScopes: botScopes,
                        }),
                      );
                      const url = `https://id.twitch.tv/oauth2/authorize?response_type=code&client_id=${botApp.clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${botScopesUrlParam}&state=${state}`;
                      navigator.clipboard.writeText(url);
                      alert(
                        "URL copied to clipboard! Open it in an incognito/private window.",
                      );
                    }}
                    style={{
                      width: "100%",
                      padding: "8px 16px",
                      backgroundColor: "#4a5568",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "13px",
                    }}
                  >
                    <Icon
                      name="clipboard-list"
                      size={16}
                      style={{ verticalAlign: "-3px", marginRight: "6px" }}
                    />
                    Copy URL to Clipboard
                  </button>
                </div>
              </div>
            ) : (
              <div
                style={{
                  backgroundColor: "#1a202c",
                  padding: "15px",
                  borderRadius: "6px",
                  border: "1px solid #ef4444",
                }}
              >
                <p style={{ color: "#ef4444", margin: 0 }}>
                  <Icon
                    name="alert-triangle"
                    size={14}
                    style={{ verticalAlign: "-2px", marginRight: "4px" }}
                  />
                  No bot app configured. Please configure it in{" "}
                  <a
                    href="/dashboard/admin/twitch-apps"
                    style={{ color: "#9146FF" }}
                  >
                    Twitch Apps
                  </a>{" "}
                  first.
                </p>
              </div>
            )}

            <div
              style={{
                display: "flex",
                gap: "10px",
                marginTop: "10px",
                alignItems: "center",
              }}
            >
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setFormData({
                    botUsername: "",
                    accessToken: "",
                    refreshToken: "",
                    twitchUserId: "",
                    autoRefresh: false,
                  });
                }}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#6B7280",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Close
              </button>
              <span style={{ color: "#a0aec0", fontSize: "12px" }}>
                You can close this when finished in the other private window
              </span>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gap: "15px" }}>
        {botCredentials.map((bot) => (
          <div
            key={bot.id}
            style={{
              backgroundColor: "#2d3748",
              padding: "20px",
              borderRadius: "8px",
              border: "1px solid #4a5568",
            }}
          >
            {editingBot === bot.botUsername ? (
              <div style={{ display: "grid", gap: "15px" }}>
                <h3 style={{ color: "#9146FF", marginTop: 0 }}>
                  Edit Bot: {bot.botUsername}
                </h3>

                {botApp && botApp.clientId ? (
                  <div style={{ marginBottom: "15px" }}>
                    <p
                      style={{
                        color: "#48BB78",
                        marginBottom: "10px",
                        textAlign: "center",
                      }}
                    >
                      <Icon
                        name="check"
                        size={14}
                        color="#10B981"
                        style={{ verticalAlign: "-2px", marginRight: "4px" }}
                      />
                      Using Bot App: {botApp.clientId}
                    </p>
                    <div
                      style={{
                        backgroundColor: "#ef4444",
                        padding: "10px",
                        borderRadius: "4px",
                        marginBottom: "15px",
                        fontSize: "12px",
                      }}
                    >
                      <strong>
                        <Icon
                          name="alert-triangle"
                          size={14}
                          style={{ verticalAlign: "-2px", marginRight: "4px" }}
                        />
                        CRITICAL:
                      </strong>{" "}
                      You MUST:
                      <ol style={{ margin: "5px 0 0 20px", paddingLeft: "0" }}>
                        <li>Open this URL in an incognito/private window</li>
                        <li>
                          Log in as <strong>{bot.botUsername}</strong> (NOT as
                          yourself!)
                        </li>
                        <li>Authorize the bot app</li>
                      </ol>
                    </div>
                    <div
                      style={{
                        backgroundColor: "#1a202c",
                        padding: "15px",
                        borderRadius: "6px",
                        border: "1px solid #4a5568",
                      }}
                    >
                      <label
                        style={{
                          color: "#cbd5e0",
                          fontSize: "12px",
                          display: "block",
                          marginBottom: "8px",
                        }}
                      >
                        OAuth URL for {bot.botUsername}:
                      </label>
                      <div
                        style={{
                          backgroundColor: "#2d3748",
                          padding: "8px",
                          borderRadius: "4px",
                          marginBottom: "10px",
                          fontSize: "11px",
                          color: "#48BB78",
                        }}
                      >
                        <strong>Scopes included:</strong>
                        <ul
                          style={{ margin: "5px 0 0 20px", paddingLeft: "0" }}
                        >
                          {botScopes.map((scope) => (
                            <li key={scope}>{scope}</li>
                          ))}
                        </ul>
                      </div>
                      <div
                        style={{
                          backgroundColor: "#2d3748",
                          padding: "10px",
                          borderRadius: "4px",
                          wordBreak: "break-all",
                          fontFamily: "monospace",
                          fontSize: "11px",
                          color: "#e2e8f0",
                          marginBottom: "10px",
                          maxHeight: "100px",
                          overflow: "auto",
                        }}
                      >
                        {(() => {
                          const redirectUri =
                            window.location.hostname === "localhost"
                              ? "http://localhost/api/auth/bot-callback"
                              : "https://hal8580.com/api/auth/bot-callback";
                          const state = btoa(
                            JSON.stringify({
                              botUsername: bot.botUsername,
                              action: "update",
                              requestedScopes: botScopes,
                            }),
                          );
                          return `https://id.twitch.tv/oauth2/authorize?response_type=code&client_id=${botApp.clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${botScopesUrlParam}&state=${state}&force_verify=true`;
                        })()}
                      </div>
                      <button
                        onClick={() => {
                          const redirectUri =
                            window.location.hostname === "localhost"
                              ? "http://localhost/api/auth/bot-callback"
                              : "https://hal8580.com/api/auth/bot-callback";
                          const state = btoa(
                            JSON.stringify({
                              botUsername: bot.botUsername,
                              action: "update",
                              requestedScopes: botScopes,
                            }),
                          );
                          const url = `https://id.twitch.tv/oauth2/authorize?response_type=code&client_id=${botApp.clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${botScopesUrlParam}&state=${state}&force_verify=true`;
                          navigator.clipboard.writeText(url);
                          alert(
                            "URL copied to clipboard! Open it in an incognito/private window.",
                          );
                        }}
                        style={{
                          width: "100%",
                          padding: "8px 16px",
                          backgroundColor: "#4a5568",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "13px",
                        }}
                      >
                        <Icon
                          name="clipboard-list"
                          size={16}
                          style={{ verticalAlign: "-3px", marginRight: "6px" }}
                        />
                        Copy URL to Clipboard
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      backgroundColor: "#1a202c",
                      padding: "15px",
                      borderRadius: "6px",
                      border: "1px solid #ef4444",
                    }}
                  >
                    <p style={{ color: "#ef4444", margin: 0 }}>
                      <Icon
                        name="alert-triangle"
                        size={14}
                        style={{ verticalAlign: "-2px", marginRight: "4px" }}
                      />
                      No bot app configured. Please configure it in{" "}
                      <a
                        href="/dashboard/admin/twitch-apps"
                        style={{ color: "#9146FF" }}
                      >
                        Twitch Apps
                      </a>{" "}
                      first.
                    </p>
                  </div>
                )}

                <div
                  style={{ display: "flex", gap: "10px", alignItems: "center" }}
                >
                  <button
                    onClick={() => setEditingBot(null)}
                    style={{
                      padding: "8px 16px",
                      backgroundColor: "#6B7280",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                  >
                    Close
                  </button>
                  <span style={{ color: "#a0aec0", fontSize: "12px" }}>
                    You can close this when finished in the other private window
                  </span>
                </div>
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "start",
                  }}
                >
                  <div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        marginBottom: "10px",
                      }}
                    >
                      <h3 style={{ color: "#e2e8f0", margin: 0 }}>
                        {bot.botUsername}
                      </h3>
                      <span
                        style={{
                          padding: "3px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "500",
                          backgroundColor: bot.isPublic ? "#4ade80" : "#9146FF",
                          color: bot.isPublic ? "#052e16" : "white",
                        }}
                      >
                        {bot.isPublic ? "Public" : "Private"}
                      </span>
                      {(bot.activeBotCount !== undefined ||
                        bot.totalBotCount !== undefined) && (
                        <span
                          style={{
                            padding: "3px 8px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontWeight: "500",
                            backgroundColor:
                              bot.activeBotCount && bot.activeBotCount > 0
                                ? "#48BB78"
                                : "#4a5568",
                            color: "white",
                          }}
                        >
                          {bot.activeBotCount || 0} / {bot.totalBotCount || 0}{" "}
                          bots active
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: "14px", color: "#a0aec0" }}>
                      <p style={{ margin: "5px 0" }}>
                        <strong>Access Token:</strong> {bot.accessToken}
                      </p>
                      <p style={{ margin: "5px 0" }}>
                        <strong>Refresh Token:</strong> {bot.refreshToken}
                      </p>
                      <p style={{ margin: "5px 0" }}>
                        <strong>Token Expires:</strong>{" "}
                        {formatDate(bot.tokenExpiresAt)}
                      </p>
                      <p style={{ margin: "5px 0" }}>
                        <strong>Last Token Refresh:</strong>{" "}
                        {bot.lastTokenRefresh
                          ? formatDate(bot.lastTokenRefresh)
                          : "Never"}
                      </p>
                      {bot.createdBy && (
                        <p style={{ margin: "5px 0" }}>
                          <strong>Created By:</strong> {bot.createdBy.username}
                        </p>
                      )}
                      <p style={{ margin: "5px 0" }}>
                        <strong>Last Updated:</strong>{" "}
                        {formatDate(bot.updatedAt)}
                      </p>
                      <p style={{ margin: "5px 0" }}>
                        <strong>Chat link (dashboard app):</strong>{" "}
                        {chatLinkStatus[bot.botUsername]
                          ? `${chatLinkStatus[bot.botUsername].status} (scopes: ${chatLinkStatus[bot.botUsername].scopes.join(", ") || "none"})`
                          : bot.dashboardTokenLinkedAt
                            ? `linked ${formatDate(bot.dashboardTokenLinkedAt)}`
                            : "not linked"}
                      </p>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <button
                      onClick={() => handleEdit(bot)}
                      style={{
                        padding: "6px 12px",
                        backgroundColor: "#4a5568",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleRefresh(bot.botUsername)}
                      style={{
                        padding: "6px 12px",
                        backgroundColor: "#3182CE",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      Refresh Token
                    </button>
                    <button
                      onClick={() => handleLinkChat(bot.botUsername)}
                      title="Log in to Twitch as this bot account and grant the dashboard app user:bot + user:read:chat"
                      style={{
                        padding: "6px 12px",
                        backgroundColor: "#9146FF",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      Link Chat
                    </button>
                    <button
                      onClick={() => checkChatLink(bot.botUsername)}
                      style={{
                        padding: "6px 12px",
                        backgroundColor: "#4a5568",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      Check Chat Link
                    </button>
                    <button
                      onClick={() => handleDelete(bot.botUsername)}
                      style={{
                        padding: "6px 12px",
                        backgroundColor: "#E53E3E",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {botCredentials.length === 0 && (
        <div
          style={{
            backgroundColor: "#2d3748",
            padding: "40px",
            borderRadius: "8px",
            textAlign: "center",
            color: "#a0aec0",
          }}
        >
          <p style={{ margin: 0 }}>No bot credentials found.</p>
          <p style={{ margin: "8px 0 0 0", fontSize: "14px" }}>
            Add bot credentials to enable HAL to connect to Twitch.
          </p>
        </div>
      )}
    </div>
  );
};

export default BotAdmin;
